import { useTexture } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { forwardRef, useImperativeHandle, useRef } from 'react';
import {
  Color,
  Group,
  Mesh,
  PerspectiveCamera,
  ShaderMaterial,
  Texture,
  Uniform,
  Vector2,
} from 'three';

import type { GroupProps } from 'types/r3f';
import { publicAssets } from 'utils/gcs';
import { textureImage } from 'utils/textureImage';

import {
  scaleBackground,
  WEATHER_SCREEN_PREFIX,
} from '../WeatherScreens.utils';
import fragmentShader from './shader.frag';
import vertexShader from './shader.vert';

interface RainScreenProps extends GroupProps {
  backgroundTexture: Texture;
}
const RainScreen = forwardRef<Group, RainScreenProps>(
  ({ backgroundTexture, ...props }, ref) => {
    const screenRef = useRef<Group>(null!);
    const backgroundRef = useRef<Mesh>(null);

    const randomnessTex = useTexture(publicAssets('/effects/random.jpeg'));
    const materialRef = useRef<ShaderMaterial>(
      new ShaderMaterial({
        vertexShader,
        fragmentShader,
        uniforms: {
          uRandomTexture: new Uniform(randomnessTex),
          uResolution: {
            value: new Vector2(
              textureImage(backgroundTexture).width,
              textureImage(backgroundTexture).height
            ),
          },
          uTime: { value: 0 },
          uBackground: new Uniform(backgroundTexture),
          uRainColor: { value: new Color(0x504f54) },
        },
      })
    );

    useFrame(({ camera, size, clock }) => {
      if (!backgroundRef.current) return;

      scaleBackground({
        mesh: backgroundRef.current,
        camera: camera as PerspectiveCamera,
        size,
        texture: backgroundTexture,
        positionZ: screenRef.current.position.z,
      });

      materialRef.current.uniforms.uRandomTexture = new Uniform(randomnessTex);
      materialRef.current.uniforms.uBackground = new Uniform(backgroundTexture);
      materialRef.current.uniforms.uTime.value = clock.getElapsedTime() / 1.5;
      materialRef.current.uniforms.uResolution.value.set(
        textureImage(backgroundTexture).width,
        textureImage(backgroundTexture).height
      );
    });

    useImperativeHandle(ref, () => screenRef.current);

    return (
      <group name={WEATHER_SCREEN_PREFIX + 'rainy'} ref={screenRef} {...props}>
        <mesh name="background" ref={backgroundRef} rotation={[Math.PI, 0, 0]}>
          <planeGeometry args={[1, 1]} />
          <primitive object={materialRef.current} attach="material" />
        </mesh>
      </group>
    );
  }
);

RainScreen.displayName = 'RainScreen';
export default RainScreen;
